<template>
  <div>
    <!-- Header: title + count, search, export -->
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <div class="flex items-baseline gap-3">
        <h2 class="text-lg font-semibold text-gray-800">Entity Summary</h2>
        <span class="text-xs text-gray-500 tabular-nums">
          {{ totalEntities.toLocaleString() }} {{ entityTypeLabel }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search..."
          class="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-48 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
        />
        <ExportMenu :disabled="!entities.length" :exports="[{ type: 'excel', action: handleExport, loading: exporting }]" />
      </div>
    </div>

    <!-- Top row: Entity Type | Group by | Filter by (non-tag) -->
    <div class="flex items-start flex-wrap gap-x-6 gap-y-2 mb-2">
      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-gray-700">Entity Type</span>
        <select
          v-model="selectedEntityType"
          class="px-2.5 py-1 text-xs border border-gray-300 rounded-full bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option v-for="t in entityTypeOptions" :key="t.value" :value="t.value">
            {{ t.label }}
          </option>
        </select>
      </div>
      <div class="flex items-center flex-wrap gap-2">
        <span class="text-xs font-medium text-gray-700">Group by</span>
        <Tag
          v-for="key in groupBy"
          :key="key"
          :label="groupByLabel(key)"
          color="primary"
          removable
          @remove="removeGroupBy(key)"
        />
        <select
          v-if="availableGroupByOptions.length > 0"
          :value="''"
          @change="onAddGroupBy"
          class="px-2.5 py-1 text-xs border border-dashed border-gray-300 rounded-full bg-white text-gray-500 hover:bg-gray-50 focus:outline-none"
        >
          <option value="" disabled>+ Group</option>
          <optgroup v-for="g in availableGroupByGrouped" :key="g.label" :label="g.label">
            <option v-for="opt in g.options" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </optgroup>
        </select>
      </div>
      <div class="flex items-center flex-wrap gap-2">
        <span class="text-xs font-medium text-gray-700">Filter by</span>
        <template v-for="typeName in activeFilterTypes" :key="typeName">
          <div class="inline-flex items-center gap-1">
            <span class="text-[11px] text-gray-500">{{ typeName }}</span>
            <MultiSelectDropdown
              :modelValue="hierarchyFilters[typeName] || []"
              @update:modelValue="updateHierarchyFilter(typeName, $event)"
              :options="hierarchyFilterOptions[typeName] || []"
              :placeholder="typeName"
              :searchable="(hierarchyFilterOptions[typeName] || []).length > 20"
              compact
            />
            <button
              @click="removeFilterType(typeName)"
              class="text-gray-400 hover:text-red-500 leading-none text-base px-0.5"
              :title="`Remove ${typeName} filter`"
            >&times;</button>
          </div>
        </template>
        <select
          v-if="availableFilterTypes.length > 0"
          :value="''"
          @change="onAddFilterType"
          class="px-2.5 py-1 text-xs border border-dashed border-gray-300 rounded-full bg-white text-gray-500 hover:bg-gray-50 focus:outline-none"
        >
          <option value="" disabled>+ Filter</option>
          <option v-for="t in availableFilterTypes" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
    </div>

    <!-- Tag filter row -->
    <div class="flex items-center flex-wrap gap-x-4 gap-y-2 mb-4">
      <div class="flex items-center gap-2">
        <span class="text-xs font-medium text-gray-700">Tag Type</span>
        <select
          v-model="selectedTagType"
          @change="onTagTypeChange"
          class="px-2.5 py-1 text-xs border border-gray-300 rounded-full bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="__all__">All</option>
          <option v-for="t in availableTagTypes" :key="t" :value="t">{{ t }}</option>
        </select>
      </div>
      <GroupedInfoTagFilter
        v-model="infoTagGroups"
        v-model:groupsMode="infoTagGroupsMode"
        :infoTagType="selectedTagType === '__all__' ? null : selectedTagType"
        label=""
        buttonLabel="Tag"
        color="emerald"
        direction="horizontal"
      />
      <button
        v-if="hasActiveFilters"
        @click="clearFilters"
        class="text-[11px] text-gray-500 hover:text-gray-700 underline ml-auto"
      >Clear all</button>
    </div>

    <!-- Table -->
    <div class="border border-growth-50 rounded-lg bg-white overflow-hidden">
      <div class="max-h-[360px] overflow-y-auto">
        <table class="w-full text-xs">
          <thead class="border-b border-growth-50 text-left text-growth-600 sticky top-0 bg-white z-[1]">
            <tr>
              <th
                v-for="col in displayColumns"
                :key="col.key"
                @click="col.sortable && toggleSort(col.key)"
                :class="[
                  'py-2 px-3 font-medium',
                  col.sortable ? 'cursor-pointer select-none hover:text-gray-900' : '',
                  col.align === 'right' ? 'text-right' : (col.align === 'center' ? 'text-center' : '')
                ]"
              >
                <span
                  class="inline-flex items-center gap-1"
                  :class="col.align === 'right' ? 'justify-end' : (col.align === 'center' ? 'justify-center' : '')"
                >
                  {{ col.label }}
                  <span v-if="col.sortable" class="text-[11px]">{{ sortIndicator(col.key) }}</span>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="displayColumns.length" class="text-center py-10 text-growth-400">Loading…</td>
            </tr>
            <template v-else>
              <tr
                v-for="item in flatComboRows"
                :key="comboRowKey(item)"
                @click="goToEntity(metricSource(item))"
                class="cursor-pointer hover:bg-gray-50 border-b border-growth-50"
              >
                <td class="py-3 px-4 text-growth-800">
                  <span class="sensitive font-medium text-neutral-700 hover:text-neutral-900 hover:underline">{{ item.row.name }}</span>
                </td>
                <td
                  v-for="key in groupBy"
                  :key="key"
                  class="py-3 px-3 whitespace-nowrap"
                  :class="item.combo[key] === noneLabel ? 'text-gray-400' : 'text-gray-700'"
                >
                  {{ item.combo[key] }}
                </td>
                <td class="py-3 px-2">
                  <div class="flex flex-wrap gap-1 items-center">
                    <span
                      v-for="tag in displayTags(metricSource(item))"
                      :key="tag.id"
                      class="inline-flex items-center gap-1.5 text-xs leading-snug px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-700 max-w-[200px]"
                      :title="tag.type ? `${tag.type}: ${tag.name}` : tag.name"
                    >
                      <span class="w-2 h-2 rounded-full shrink-0" :style="{ backgroundColor: tag.color || '#6b7280' }"></span>
                      <span class="truncate">{{ tag.name }}</span>
                    </span>
                    <span
                      v-if="tagOverflowCount(metricSource(item)) > 0"
                      class="text-[11px] text-growth-600 ml-1"
                      :title="overflowTagsTooltip(metricSource(item))"
                    >+{{ tagOverflowCount(metricSource(item)) }}</span>
                  </div>
                </td>
                <td class="py-3 px-2 text-center">
                  <CC_Engagement_Score_Badge :params="{ value: metricSource(item).engagement_score }" />
                </td>
                <td class="py-3 px-2 text-right text-growth-800 tabular-nums">
                  {{ formatNum(comboAttribution(item.row, item.combo)) }}
                </td>
                <td class="py-3 px-2 text-right text-growth-600">
                  {{ formatDate(metricSource(item).last_engagement_date) }}
                </td>
              </tr>
              <tr v-if="!loading && flatComboRows.length === 0">
                <td :colspan="displayColumns.length" class="text-center py-10 text-growth-400">No entities found</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Reporting (tabbed) -->
    <EntityReporting
      class="mt-4 border border-gray-200 rounded-lg bg-white"
      :allEntities="entityRowsAfterHierarchyFilter"
      :filteredEntities="entityRowsAfterTagFilter"
      :tagGroups="chartTagGroups"
      :groupsMode="infoTagGroupsMode"
      :infoTagLookup="infoTagLookup"
    />
  </div>
</template>

<script>
import axios from 'axios'
import { exportToExcel } from '@/utils/exportArraytoXlsx'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import CC_Engagement_Score_Badge from '@/components/widgetcomponents/AG_Grid_Custom_Cells/CC_Engagement_Score_Badge.vue'
import GroupedInfoTagFilter from '@/components/widgetcomponents/Dropdown_Selector/GroupedInfoTagFilter.vue'
import MultiSelectDropdown from '@/components/widgetcomponents/library/MultiSelectDropdown.vue'
import Tag from '@/components/widgetcomponents/library/Tag.vue'
import EntityReporting from '@/components/ConfigurablePageComponents/Engage/EntityReporting.vue'

const TAG_PREFIX = 'tag:'
const ANCESTOR_PREFIX = 'ancestor:'
const UNTAGGED_VALUE = '(none)'

// Entity types that are not part of the practice/CRM navigation hierarchy.
// Excluded from the dropdown and from the entity_tree fetch so high-volume
// leaf types (e.g. 25k+ Patients on Vytalize) don't bloat every request.
const EXCLUDED_ENTITY_TYPES = new Set(['Patient', 'Staff'])

// Entity types that carry their own engagement score. Everything else (TIN,
// Location, Contract, ...) is a structural node: EngagementScoreService returns
// 0.0 for it rather than null, because _compute_composite divides by the full
// sub-metric weight even when every sub-score is missing. Averaging those in
// dilutes a practice by however complete its hierarchy happens to be — see
// ticket 2334, where a practice reading 2.6 on its own page showed 0.9 here.
const SCOREABLE_ENTITY_TYPES = new Set(['Practice'])

export default {
  name: 'Practice_Tag_Summary',
  components: {
    ExportMenu,
    CC_Engagement_Score_Badge,
    GroupedInfoTagFilter,
    MultiSelectDropdown,
    Tag,
    EntityReporting,
  },
  data() {
    return {
      loading: true,
      exporting: false,
      entities: [],
      entityMap: {},
      parentMap: {},
      childMap: {},
      allEntityTypeOptions: [],
      selectedEntityType: 'Practice',
      activeFilterTypes: [],
      hierarchyFilters: {},
      infoTagGroups: [],
      infoTagGroupsMode: 'or',
      filterTagCache: {},
      groupBy: [],
      selectedTagType: '__all__',
      searchQuery: '',
      sortColumn: 'name',
      sortDirection: 'asc',
      tagDisplayLimit: 6,
    }
  },
  computed: {
    // Entity types offered as ROW types are limited to those patients partition across
    // -- no attribution leaf (TIN) has more than one ancestor of that type. This keeps
    // Practice and the org-management chain (Market, Practice Liaison, Physician Org,
    // Regions...) and drops the contract branch (Payer, Contract Pool, Cohort, Contract,
    // Line of Business, CPY), where a TIN attaches to many by design so any total would
    // double-count. Rejected types stay available as group-by dimensions. A deliberately
    // strict, uniform rule: a few org roles with rare double-assignment are dropped too.
    partitionInvalidTypes() {
      const tins = this.entities.filter(e => e.entity_type === 'TIN')
      if (!tins.length) return new Set()
      const invalid = new Set()
      for (const tin of tins) {
        const counts = {}
        for (const a of this.walkAncestors(tin.id)) {
          counts[a.entity_type] = (counts[a.entity_type] || 0) + 1
          if (counts[a.entity_type] > 1) invalid.add(a.entity_type)
        }
      }
      return invalid
    },
    entityTypeOptions() {
      const invalid = this.partitionInvalidTypes
      return this.allEntityTypeOptions.filter(o => !invalid.has(o.value))
    },
    entityTypeLabel() {
      const opt = this.entityTypeOptions.find(o => o.value === this.selectedEntityType)
      const base = opt?.label || this.selectedEntityType
      return base.endsWith('s') ? base : base + 's'
    },
    entityRowsBaseline() {
      const rows = this.entities.filter(e => e.entity_type === this.selectedEntityType)
      const tagTypes = this.availableTagTypes
      return rows.map(e => {
        const allTags = e.tags || []
        const relatedByType = this.relatedDimensionsFor(e.id)
        const ancestorByType = {}
        for (const [type, items] of Object.entries(relatedByType)) {
          ancestorByType[type] = items.map(i => i.name).join(', ')
        }
        const ancestorIds = new Set()
        for (const items of Object.values(relatedByType)) {
          for (const i of items) ancestorIds.add(i.id)
        }
        const tagsByType = {}
        for (const t of tagTypes) {
          tagsByType[t] = allTags.filter(tag => tag.type === t).map(tag => tag.name).join(', ')
        }
        // Engagement roll-up: lives-weighted avg composite + max last_engagement
        // across self + descendants. Only SCOREABLE_ENTITY_TYPES are members --
        // structural descendants score a hard 0.0 and would otherwise drag the
        // average down (ticket 2334). A Practice therefore degenerates to its
        // own score, since its only scoreable descendant is itself; Market /
        // Practice Liaison / Physician Organization / Market Leaders / Central
        // Support Staff still aggregate the practices beneath them.
        //
        // Contract Pool / Contract (Cohort) / Payer / Line of Business reach no
        // Practice at all -- they attach to the TIN, which the Practice also
        // parents, so walking down from a contract stops at the shared TIN.
        // Those rows now read blank instead of a fabricated 0.0. Making them
        // aggregate for real needs a TIN pivot -- see ticket 2682.
        let scoreNum = 0
        let scoreDen = 0
        let maxLast = null
        for (const id of this.descendantsOf(e.id)) {
          const d = this.entityMap[id]
          if (!d) continue
          if (SCOREABLE_ENTITY_TYPES.has(d.entity_type)) {
            const score = d.engagement_score?.composite
            const lives = d.attribution?.total_attribution || 0
            if (score != null) {
              const w = lives > 0 ? lives : 1
              scoreNum += score * w
              scoreDen += w
            }
          }
          // Last touch is deliberately NOT gated by type: an engagement logged
          // against any node in the subtree is still a touch on this entity.
          if (d.last_engagement_date) {
            if (!maxLast || new Date(d.last_engagement_date) > new Date(maxLast)) {
              maxLast = d.last_engagement_date
            }
          }
        }
        const rolledComposite = scoreDen > 0 ? scoreNum / scoreDen : null
        // Only attach the breakdown when this row IS the scored entity. For a
        // roll-up parent the composite spans several practices, so a single
        // entity's sub-scores cannot explain it -- that mismatch is what made
        // 2334 present as "hover is right, number is wrong".
        const engagementScore = rolledComposite != null
          ? {
              composite: rolledComposite,
              sub_scores: SCOREABLE_ENTITY_TYPES.has(e.entity_type)
                ? (e.engagement_score?.sub_scores || {})
                : {}
            }
          : null
        return {
          entity_id: e.id,
          name: e.name,
          tags: allTags,
          ancestorByType,
          tagsByType,
          relatedByType,
          ancestor_ids: ancestorIds,
          engagement_score: engagementScore,
          engagement_composite: rolledComposite,
          attributed_lives: e.attribution?.total_attribution ?? 0,
          by_contract: e.attribution?.by_contract || {},
          last_engagement_date: maxLast,
        }
      })
    },
    entityRowsAfterHierarchyFilter() {
      let result = this.entityRowsBaseline
      if (this.hasActiveHierarchyFilters) {
        const selectedIds = [...new Set(Object.values(this.hierarchyFilters).flat())]
        result = result.filter(r =>
          selectedIds.every(id => r.ancestor_ids.has(id) || r.entity_id === id)
        )
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase()
        result = result.filter(r => r.name?.toLowerCase().includes(q))
      }
      return result
    },
    entityRowsAfterTagFilter() {
      if (!this.hasActiveTagFilter) return this.entityRowsAfterHierarchyFilter
      const groups = this.activeTagGroups
      const groupsMode = this.infoTagGroupsMode === 'and' ? 'and' : 'or'
      return this.entityRowsAfterHierarchyFilter.filter(r => {
        const tagIds = new Set((r.tags || []).map(t => t.id))
        const groupResults = groups.map(g => {
          const include = g.include || []
          const exclude = g.exclude || []
          const mode = g.mode === 'and' ? 'and' : 'or'
          const incOk = include.length === 0
            ? true
            : (mode === 'and' ? include.every(tid => tagIds.has(tid)) : include.some(tid => tagIds.has(tid)))
          const excOk = !exclude.some(tid => tagIds.has(tid))
          return incOk && excOk
        })
        return groupsMode === 'and' ? groupResults.every(Boolean) : groupResults.some(Boolean)
      })
    },
    availableTagTypes() {
      const types = new Set()
      for (const e of this.entities) {
        if (e.entity_type !== this.selectedEntityType) continue
        for (const t of (e.tags || [])) if (t.type) types.add(t.type)
      }
      return [...types].sort()
    },
    hierarchyFilterOptions() {
      const seen = {}
      for (const r of this.entityRowsBaseline) {
        for (const [type, items] of Object.entries(r.relatedByType || {})) {
          if (!seen[type]) seen[type] = new Map()
          for (const item of items) {
            if (!seen[type].has(item.id)) {
              seen[type].set(item.id, { value: item.id, label: item.name })
            }
          }
        }
      }
      const result = {}
      for (const [type, m] of Object.entries(seen)) {
        const opts = [...m.values()].sort((a, b) => a.label.localeCompare(b.label))
        if (opts.length > 1) result[type] = opts
      }
      return result
    },
    filterDimensionTypes() {
      return Object.keys(this.hierarchyFilterOptions).sort()
    },
    availableFilterTypes() {
      return this.filterDimensionTypes.filter(t => !this.activeFilterTypes.includes(t))
    },
    hasActiveHierarchyFilters() {
      return Object.values(this.hierarchyFilters).some(ids => ids.length > 0)
    },
    activeTagGroups() {
      return (this.infoTagGroups || []).filter(g =>
        (g.include?.length || 0) > 0 || (g.exclude?.length || 0) > 0
      )
    },
    hasActiveTagFilter() {
      return this.activeTagGroups.length > 0
    },
    // Drives the EntityTagCoverage chart. When Tag Type is set without explicit tag
    // chips, synthesize a group containing every tag of that type so the chart shows
    // the type's breakdown. Explicit chips override.
    chartTagGroups() {
      if (this.activeTagGroups.length > 0) return this.activeTagGroups
      if (this.selectedTagType !== '__all__') {
        const ids = Object.values(this.infoTagLookup)
          .filter(t => t.type === this.selectedTagType)
          .map(t => t.id)
        if (ids.length > 0) return [{ include: ids, exclude: [], mode: 'or' }]
      }
      return []
    },
    hasActiveFilters() {
      return this.hasActiveHierarchyFilters || this.hasActiveTagFilter || !!this.searchQuery || this.activeFilterTypes.length > 0
    },
    infoTagLookup() {
      const lookup = { ...this.filterTagCache }
      for (const entity of this.entities) {
        for (const tag of (entity.tags || [])) {
          if (tag.id) {
            lookup[tag.id] = { id: tag.id, name: tag.name, type: tag.type, color: tag.color }
          }
        }
      }
      return lookup
    },
    // Classify each hierarchy dimension as 'up' (ancestor / reachable by walking up)
    // or 'down' (a descendant of the selected entity). Grouping resolves an up dim by
    // walking up (anchor engine); a down dim by fanning over descendants.
    dimDirections() {
      const down = new Set()
      const up = new Set()
      for (const e of this.entities) {
        if (e.entity_type !== this.selectedEntityType) continue
        for (const id of this.descendantsOf(e.id)) {
          const t = this.entityMap[id]?.entity_type
          if (t && t !== this.selectedEntityType) down.add(t)
        }
        for (const a of this.anchorsFor(e.id)) {
          for (const t of Object.keys(a)) up.add(t)
        }
      }
      const dir = {}
      for (const t of down) dir[t] = 'down'
      for (const t of up) if (!(t in dir)) dir[t] = 'up'
      return dir
    },
    // Direction of the active grouping (down if any selected ancestor dim is a
    // descendant type). A grouping is kept single-direction by the option guard.
    groupingDirection() {
      for (const k of this.groupBy) {
        if (k.startsWith(ANCESTOR_PREFIX) && this.dimDirections[k.slice(ANCESTOR_PREFIX.length)] === 'down') return 'down'
      }
      return 'up'
    },
    groupByOptions() {
      const tagOpts = this.availableTagTypes.map(t => ({
        value: TAG_PREFIX + t,
        label: t,
        group: 'Tag Type',
      }))
      // Offer a hierarchy dim only if it's resolvable: any descendant type (down),
      // or an up type that also passes the >1-value filter (avoids single-group dims).
      const ancestorOpts = Object.keys(this.dimDirections)
        .filter(t => this.dimDirections[t] === 'down' || this.filterDimensionTypes.includes(t))
        .sort()
        .map(t => ({
          value: ANCESTOR_PREFIX + t,
          label: t,
          dir: this.dimDirections[t],
          group: this.dimDirections[t] === 'down' ? 'Contains (below)' : 'Hierarchy (above)',
        }))
      return [...tagOpts, ...ancestorOpts]
    },
    // Same-direction guard: a grouping mixes neither up+down hierarchy dims nor more
    // than one down dim (a down dim already pivots the row to that descendant). Tags
    // are direction-neutral and always offered.
    availableGroupByOptions() {
      const hasDown = this.groupBy.some(k => k.startsWith(ANCESTOR_PREFIX) && this.dimDirections[k.slice(ANCESTOR_PREFIX.length)] === 'down')
      const hasUp = this.groupBy.some(k => k.startsWith(ANCESTOR_PREFIX) && this.dimDirections[k.slice(ANCESTOR_PREFIX.length)] === 'up')
      return this.groupByOptions.filter(o => {
        if (this.groupBy.includes(o.value)) return false
        if (o.dir == null) return true
        if (hasDown) return false
        if (hasUp) return o.dir === 'up'
        return true
      })
    },
    availableGroupByGrouped() {
      const groups = {}
      for (const opt of this.availableGroupByOptions) {
        const g = opt.group || 'Other'
        if (!groups[g]) groups[g] = []
        groups[g].push(opt)
      }
      return Object.entries(groups).map(([label, options]) => ({ label, options }))
    },
    // Tag types currently in scope: selected Tag Type + any tag types referenced by tag filters.
    // When non-empty, the Tags column only shows tags of these types.
    activeTagTypes() {
      const set = new Set()
      if (this.selectedTagType !== '__all__') set.add(this.selectedTagType)
      for (const g of this.infoTagGroups || []) {
        const ids = [...(g.include || []), ...(g.exclude || [])]
        for (const id of ids) {
          const t = this.infoTagLookup[id]?.type
          if (t) set.add(t)
        }
      }
      return [...set]
    },
    totalEntities() {
      return this.entityRowsAfterTagFilter.length
    },
    noneLabel() {
      return UNTAGGED_VALUE
    },
    // Maps each attribution CONTRACT_ID to the graph entity that carries it as an
    // identifier -- the Contract Pool on Diverge, the Contract on Vytalize. Walking
    // up from that entity (anchorTupleFor) yields the dimensions attribution can be
    // split by (Payer, Market, ...); anything below it in the graph (Diverge's Line
    // of Business / Cohort) can't subdivide the number.
    contractIdLookup() {
      const map = {}
      for (const e of this.entities) {
        const cid = e.identifiers?.CONTRACT_ID
        if (cid != null && !(cid in map)) map[cid] = e.id
      }
      return map
    },
    // One leading column per group-by dimension (path-accurate value per row),
    // then the entity, its tags, and the practice-level metric columns.
    displayColumns() {
      const cols = [{ key: 'name', label: this.entityTypeLabel.replace(/s$/, ''), sortable: true, align: 'left' }]
      for (const key of this.groupBy) {
        cols.push({ key, label: this.groupByLabel(key), sortable: true, align: 'left' })
      }
      cols.push({ key: 'tags', label: 'Tags', sortable: false, align: 'left' })
      cols.push({ key: 'engagement', label: 'Engagement', sortable: true, align: 'center' })
      cols.push({ key: 'attributed', label: 'Attributed Lives', sortable: true, align: 'right' })
      cols.push({ key: 'last_engagement', label: 'Last Engagement', sortable: true, align: 'right' })
      return cols
    },
    // Flat, unpivoted rows: one per (entity x real group-by combination).
    // With no group-by this is just one row per entity. Metrics are still
    // entity-level and repeat across an entity's combo rows -- per-combo
    // aggregation is a separate step.
    flatComboRows() {
      const base = this.entityRowsAfterTagFilter
      let flat
      if (this.groupBy.length === 0) {
        flat = base.map(r => ({ row: r, combo: {} }))
      } else {
        flat = []
        for (const r of base) {
          for (const combo of this.fanOut(r)) flat.push({ row: r, combo })
        }
      }
      const dir = this.sortDirection === 'asc' ? 1 : -1
      return flat.sort((a, b) => {
        const va = this.getSortValue(a)
        const vb = this.getSortValue(b)
        if (va == null && vb == null) { /* fall through to tie-break */ }
        else if (va == null) return 1
        else if (vb == null) return -1
        else {
          const cmp = typeof va === 'string' ? va.localeCompare(vb) : (va - vb)
          if (cmp !== 0) return dir * cmp
        }
        const sig = it => this.groupBy.map(k => it.combo[k]).join('|~|')
        return (a.row.name || '').localeCompare(b.row.name || '')
          || sig(a).localeCompare(sig(b))
          || (a.row.entity_id - b.row.entity_id)
      })
    },
  },
  watch: {
    selectedEntityType() {
      this.activeFilterTypes = []
      this.hierarchyFilters = {}
      this.groupBy = []
      this.selectedTagType = '__all__'
      this.infoTagGroups = []
      this._descCache = {}
      this._ancCache = {}
      this._anchorCache = {}
      this._tupleCache = {}
      this._contribCache = {}
    },
  },
  async mounted() {
    // Need entity types resolved before fetchData so we can scope the
    // entity_tree call via the entity_types param.
    await this.fetchEntityTypes()
    await Promise.all([this.fetchData(), this.fetchAllInfoTags()])
  },
  methods: {
    formatNum(v) {
      return (v == null ? 0 : Number(v)).toLocaleString()
    },
    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      if (isNaN(d)) return '—'
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
      const days = Math.floor((new Date() - d) / 86400000)
      return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()} (${days}d)`
    },
    async fetchData() {
      this.loading = true
      try {
        const params = { include_attribution: true }
        // Fetch the full graph (all offered types) so the partition test and the
        // up/down traversals have every branch; the Entity Type selector then
        // narrows to partition-valid row types.
        const types = this.allEntityTypeOptions.map(o => o.value).filter(Boolean)
        if (types.length > 0) params.entity_types = types.join(',')
        const response = await axios.get('/api/entities/entity_tree/', { params })
        const byId = {}
        const parentMap = {}
        for (const node of response.data || []) {
          if (!byId[node.id]) {
            const { parentId, ...rest } = node
            byId[node.id] = rest
          }
          if (node.parentId) {
            if (!parentMap[node.id]) parentMap[node.id] = []
            if (!parentMap[node.id].includes(node.parentId)) parentMap[node.id].push(node.parentId)
          }
        }
        const childMap = {}
        for (const [childId, parents] of Object.entries(parentMap)) {
          for (const pid of parents) {
            if (!childMap[pid]) childMap[pid] = []
            if (!childMap[pid].includes(Number(childId))) childMap[pid].push(Number(childId))
          }
        }
        this.entityMap = byId
        this.parentMap = parentMap
        this.childMap = childMap
        this._descCache = {}
        this._ancCache = {}
        this._anchorCache = {}
        this._tupleCache = {}
        this._contribCache = {}
        this.entities = Object.values(byId)
      } finally {
        this.loading = false
      }
    },
    async fetchEntityTypes() {
      const response = await axios.get('/api/entity-types/', { params: { include_counts: true } })
      const opts = (response.data || [])
        .filter(t => t.is_active && !EXCLUDED_ENTITY_TYPES.has(t.type_name))
        .map(t => ({ value: t.type_name, label: t.type_name }))
        .sort((a, b) => a.label.localeCompare(b.label))
      const practiceIdx = opts.findIndex(o => o.value === 'Practice')
      if (practiceIdx > 0) {
        const [practice] = opts.splice(practiceIdx, 1)
        opts.unshift(practice)
      }
      this.allEntityTypeOptions = opts
      if (opts.length && !opts.find(o => o.value === this.selectedEntityType)) {
        this.selectedEntityType = opts[0].value
      }
    },
    async fetchAllInfoTags() {
      const [tagsRes, typesRes] = await Promise.all([
        axios.get('/api/infotags/'),
        axios.get('/api/infotagtypes/'),
      ])
      const typeNameMap = {}
      for (const t of (typesRes.data || [])) typeNameMap[t.id] = t.name
      const cache = {}
      for (const tag of (tagsRes.data || [])) {
        cache[tag.id] = { id: tag.id, name: tag.name, type: typeNameMap[tag.infoTagType] || 'Other', color: null }
      }
      this.filterTagCache = cache
    },
    descendantsOf(entityId) {
      if (this._descCache && this._descCache[entityId]) return this._descCache[entityId]
      if (!this._descCache) this._descCache = {}
      const set = new Set()
      const queue = [entityId]
      while (queue.length) {
        const id = queue.shift()
        if (set.has(id)) continue
        set.add(id)
        for (const c of (this.childMap[id] || [])) queue.push(c)
      }
      this._descCache[entityId] = set
      return set
    },
    walkAncestors(entityId) {
      if (this._ancCache && this._ancCache[entityId]) return this._ancCache[entityId]
      if (!this._ancCache) this._ancCache = {}
      const ancestors = []
      const visited = new Set([entityId])
      const queue = [...(this.parentMap[entityId] || [])]
      while (queue.length) {
        const pid = queue.shift()
        if (visited.has(pid)) continue
        visited.add(pid)
        const parent = this.entityMap[pid]
        if (!parent) continue
        ancestors.push(parent)
        if (this.parentMap[pid]) queue.push(...this.parentMap[pid])
      }
      this._ancCache[entityId] = ancestors
      return ancestors
    },
    relatedDimensionsFor(entityId) {
      const result = {}
      const add = (typeName, id, name) => {
        if (!typeName || typeName === this.selectedEntityType) return
        if (id === entityId) return
        if (!result[typeName]) result[typeName] = []
        if (!result[typeName].find(x => x.id === id)) result[typeName].push({ id, name })
      }
      const visited = new Set([entityId])
      const subtree = [entityId, ...this.descendantsOf(entityId)]
      for (const nodeId of subtree) {
        for (const a of this.walkAncestors(nodeId)) {
          if (visited.has(a.id)) continue
          visited.add(a.id)
          add(a.entity_type, a.id, a.name)
        }
      }
      return result
    },
    onAddFilterType(event) {
      const value = event.target.value
      if (!value) return
      if (!this.activeFilterTypes.includes(value)) {
        this.activeFilterTypes = [...this.activeFilterTypes, value]
      }
      event.target.value = ''
    },
    removeFilterType(typeName) {
      this.activeFilterTypes = this.activeFilterTypes.filter(t => t !== typeName)
      const next = { ...this.hierarchyFilters }
      delete next[typeName]
      this.hierarchyFilters = next
    },
    onAddGroupBy(event) {
      const value = event.target.value
      if (!value) return
      if (!this.groupBy.includes(value)) this.groupBy = [...this.groupBy, value]
      event.target.value = ''
    },
    onTagTypeChange() {
      // Drop existing tag filters that no longer match the scoped type.
      this.infoTagGroups = []
    },
    removeGroupBy(key) {
      this.groupBy = this.groupBy.filter(k => k !== key)
    },
    groupByLabel(key) {
      if (key.startsWith(TAG_PREFIX)) return `Tag: ${key.slice(TAG_PREFIX.length)}`
      if (key.startsWith(ANCESTOR_PREFIX)) return key.slice(ANCESTOR_PREFIX.length)
      return key
    },
    updateHierarchyFilter(typeName, selectedIds) {
      this.hierarchyFilters = { ...this.hierarchyFilters, [typeName]: selectedIds }
    },
    clearFilters() {
      this.activeFilterTypes = []
      this.hierarchyFilters = {}
      this.infoTagGroups = []
      this.selectedTagType = '__all__'
      this.searchQuery = ''
    },
    toggleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = column
        this.sortDirection = 'asc'
      }
    },
    sortIndicator(column) {
      if (this.sortColumn !== column) return ''
      return this.sortDirection === 'asc' ? '▲' : '▼'
    },
    getSortValue(item) {
      const col = this.sortColumn
      if (col && (col.startsWith(ANCESTOR_PREFIX) || col.startsWith(TAG_PREFIX))) {
        return item.combo[col] || ''
      }
      const m = this.metricSource(item)
      switch (col) {
        case 'name': return item.row.name || ''
        case 'engagement': return m.engagement_composite
        case 'attributed': return this.comboAttribution(item.row, item.combo)
        case 'last_engagement': return m.last_engagement_date || null
        default: return item.row.name || ''
      }
    },
    visibleTagsFor(row) {
      const all = row.tags || []
      const types = this.activeTagTypes
      return types.length === 0 ? all : all.filter(t => types.includes(t.type))
    },
    displayTags(row) {
      return this.visibleTagsFor(row).slice(0, this.tagDisplayLimit)
    },
    tagOverflowCount(row) {
      return Math.max(0, this.visibleTagsFor(row).length - this.tagDisplayLimit)
    },
    overflowTagsTooltip(row) {
      const overflow = this.visibleTagsFor(row).slice(this.tagDisplayLimit)
      return overflow.map(t => t.type ? `${t.type}: ${t.name}` : t.name).join('\n')
    },
    // One value per ancestor type reachable by walking UP from a node (the node
    // itself plus its ancestors). Used both for grouping anchors and for tracing a
    // contract entity to the dimensions attribution can be split by.
    anchorTupleFor(nodeId) {
      if (!this._tupleCache) this._tupleCache = {}
      if (this._tupleCache[nodeId]) return this._tupleCache[nodeId]
      const byType = {}
      for (const n of [this.entityMap[nodeId], ...this.walkAncestors(nodeId)]) {
        if (!n || n.entity_type === this.selectedEntityType) continue
        if (!(n.entity_type in byType)) byType[n.entity_type] = n.name
      }
      this._tupleCache[nodeId] = byType
      return byType
    },
    // Membership anchors for an entity, covering both graph shapes at once: its own
    // DIRECT parents (dims that hang straight above it -- Vytalize's CPY chain and
    // org dims) plus its direct children's non-org parents (dims that hang below via
    // a shared child -- Diverge's TIN -> Contract Cohort). Excluding own-ancestors
    // and same-type nodes keeps a shared structural node (a Location shared with a
    // neighbouring practice) from bridging in a foreign entity's contracts.
    anchorsFor(entityId) {
      if (!this._anchorCache) this._anchorCache = {}
      if (this._anchorCache[entityId]) return this._anchorCache[entityId]
      const ids = new Set(this.parentMap[entityId] || [])
      const ownAncestorIds = new Set(this.walkAncestors(entityId).map(a => a.id))
      for (const child of (this.childMap[entityId] || [])) {
        for (const c of (this.parentMap[child] || [])) {
          if (c === entityId) continue
          if (ownAncestorIds.has(c)) continue
          if (this.entityMap[c]?.entity_type === this.selectedEntityType) continue
          ids.add(c)
        }
      }
      const anchors = [...ids].map(id => this.anchorTupleFor(id))
      this._anchorCache[entityId] = anchors
      return anchors
    },
    // Within a family, drop a tuple that another, more-specific tuple subsumes:
    // same non-(none) values but fewer (none)s. Removes the partial rows a
    // dual-provider dimension (e.g. Market reached via both a cohort and a direct
    // parent) would otherwise inject.
    dedupeSubsume(tuples, dims) {
      const seen = new Set()
      const uniq = []
      for (const t of tuples) {
        const sig = dims.map(d => t[d]).join('|~|')
        if (seen.has(sig)) continue
        seen.add(sig)
        uniq.push(t)
      }
      const nones = t => dims.filter(d => t[d] === UNTAGGED_VALUE).length
      return uniq.filter(t => !uniq.some(u =>
        u !== t &&
        dims.every(d => u[d] === t[d] || t[d] === UNTAGGED_VALUE) &&
        nones(u) < nones(t)
      ))
    },
    // Distinct real combinations of the selected ancestor dims for an entity.
    // Dims that co-occur on an anchor are LINKED (stay together, so chained values
    // like Payer/Contract/CPY or Payer/Pool/LOB never cross-fabricate); dims from
    // independent anchors CROSS-PRODUCT.
    ancestorCombosFor(entityId, ancDims) {
      if (ancDims.length === 0) return [{}]
      const partials = []
      for (const a of this.anchorsFor(entityId)) {
        const t = {}
        let has = false
        for (const d of ancDims) { if (a[d] != null) { t[d] = a[d]; has = true } }
        if (has) partials.push(t)
      }
      const parent = {}
      for (const d of ancDims) parent[d] = d
      const find = x => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x] } return x }
      for (const t of partials) {
        const ds = Object.keys(t)
        for (let i = 1; i < ds.length; i++) parent[find(ds[i])] = find(ds[0])
      }
      const famMap = {}
      for (const d of ancDims) {
        const r = find(d)
        if (!famMap[r]) famMap[r] = []
        famMap[r].push(d)
      }
      const famTuples = Object.values(famMap).map(fam => {
        const tuples = []
        for (const t of partials) {
          if (!fam.some(d => d in t)) continue
          const tup = {}
          for (const d of fam) tup[d] = (d in t) ? t[d] : UNTAGGED_VALUE
          tuples.push(tup)
        }
        let out = this.dedupeSubsume(tuples, fam)
        if (out.length === 0) { const z = {}; for (const d of fam) z[d] = UNTAGGED_VALUE; out = [z] }
        return out
      })
      return famTuples.reduce((acc, tuples) => {
        const next = []
        for (const c of acc) for (const t of tuples) next.push({ ...c, ...t })
        return next
      }, [{}])
    },
    // The entity's per-contract attribution, each mapped to the contract entity's
    // resolvable dimensions (Payer, etc.). One place owns the CONTRACT_ID -> dims trace.
    contribsFor(row) {
      if (!this._contribCache) this._contribCache = {}
      if (this._contribCache[row.entity_id]) return this._contribCache[row.entity_id]
      const byc = row.by_contract || {}
      const out = []
      for (const cid of Object.keys(byc)) {
        const eid = this.contractIdLookup[cid]
        if (eid == null) continue
        out.push({ cnt: byc[cid], tuple: this.anchorTupleFor(eid) })
      }
      this._contribCache[row.entity_id] = out
      return out
    },
    dedupeCombos(combos, keys) {
      const seen = new Set()
      const out = []
      for (const c of combos) {
        const sig = keys.map(k => (c[k] != null ? c[k] : UNTAGGED_VALUE)).join('|~|')
        if (seen.has(sig)) continue
        seen.add(sig)
        out.push(c)
      }
      return out
    },
    // Attribution combos the graph doesn't already produce: a contract the entity is
    // attributed under whose value isn't among its graph memberships (e.g. Vytalize
    // enrollment lagging attribution, or a contract not linked to a Payer -> a (none)
    // row). Ensures every attributed life has a home so the column always reconciles.
    residualCombosFor(row, ancDims, graphCombos) {
      if (ancDims.length === 0) return []
      const contribs = this.contribsFor(row)
      const rdims = ancDims.filter(d => contribs.some(c => d in c.tuple))
      if (rdims.length === 0) return []
      const out = []
      const seen = new Set()
      for (const c of contribs) {
        const proj = {}
        for (const d of ancDims) {
          proj[ANCESTOR_PREFIX + d] = rdims.includes(d) ? (c.tuple[d] != null ? c.tuple[d] : UNTAGGED_VALUE) : UNTAGGED_VALUE
        }
        if (graphCombos.some(g => rdims.every(d => g[ANCESTOR_PREFIX + d] === proj[ANCESTOR_PREFIX + d]))) continue
        const sig = ancDims.map(d => proj[ANCESTOR_PREFIX + d]).join('|~|')
        if (seen.has(sig)) continue
        seen.add(sig)
        out.push(proj)
      }
      return out
    },
    // Downward grouping: fan the entity over its descendants of the (single) selected
    // descendant type. The descendant is the group value and, via __pivotId, supplies
    // the row's metrics (its own attribution/engagement) -- a clean drill-down that
    // sums to the parent's total. The selected entity stays the row/name column.
    downCombos(row, pivotType) {
      const out = []
      for (const id of this.descendantsOf(row.entity_id)) {
        if (id === row.entity_id) continue
        const n = this.entityMap[id]
        if (!n || n.entity_type !== pivotType) continue
        out.push({ [ANCESTOR_PREFIX + pivotType]: n.name, __pivotId: id })
      }
      return out
    },
    // The object a row reads its metrics from: the pivot descendant for a downward
    // combo, otherwise the row entity itself.
    metricSource(item) {
      const pid = item.combo && item.combo.__pivotId
      if (pid != null) {
        const n = this.entityMap[pid] || {}
        return {
          entity_id: pid,
          name: n.name,
          engagement_score: n.engagement_score || null,
          engagement_composite: n.engagement_score ? n.engagement_score.composite : null,
          attributed_lives: n.attribution ? (n.attribution.total_attribution || 0) : 0,
          last_engagement_date: n.last_engagement_date || null,
          tags: n.tags || [],
        }
      }
      return item.row
    },
    // Explode one entity row into its distinct real group-by combinations.
    fanOut(row) {
      const keys = this.groupBy
      const ancKeys = keys.filter(k => k.startsWith(ANCESTOR_PREFIX))
      const ancDims = ancKeys.map(k => k.slice(ANCESTOR_PREFIX.length))
      const tagKeys = keys.filter(k => k.startsWith(TAG_PREFIX))
      let combos
      if (this.groupingDirection === 'down') {
        // Guard keeps this to one descendant dim, which pivots the row.
        combos = this.downCombos(row, ancDims[0])
        for (const key of tagKeys) {
          const t = key.slice(TAG_PREFIX.length)
          combos = combos.flatMap(c => {
            const tags = (this.entityMap[c.__pivotId] || {}).tags || []
            const vals = [...new Set(tags.filter(tg => tg.type === t).map(tg => tg.name))]
            return (vals.length ? vals : [UNTAGGED_VALUE]).map(v => ({ ...c, [key]: v }))
          })
        }
        return combos
      }
      const graph = this.ancestorCombosFor(row.entity_id, ancDims).map(c => {
        const o = {}
        for (const d of ancDims) o[ANCESTOR_PREFIX + d] = c[d] != null ? c[d] : UNTAGGED_VALUE
        return o
      })
      combos = this.dedupeCombos([...graph, ...this.residualCombosFor(row, ancDims, graph)], ancKeys)
      for (const key of tagKeys) {
        const t = key.slice(TAG_PREFIX.length)
        const vals = [...new Set((row.tags || []).filter(tg => tg.type === t).map(tg => tg.name))]
        const use = vals.length ? vals : [UNTAGGED_VALUE]
        combos = combos.flatMap(c => use.map(v => ({ ...c, [key]: v })))
      }
      return this.dedupeCombos(combos, keys)
    },
    comboRowKey(item) {
      const sig = this.groupBy.map(k => item.combo[k]).join('|~|')
      const pivot = item.combo && item.combo.__pivotId != null ? `#${item.combo.__pivotId}` : ''
      return `${item.row.entity_id}::${sig}${pivot}`
    },
    // Attribution for one combo row. Attribution resolves only to the grain of the
    // entity that carries the CONTRACT_ID (Payer / Market / Pool / Contract). It
    // splits cleanly and sums to the total for those dims; for a dim finer than that
    // grain (Diverge's Line of Business) it can't subdivide, so the number repeats
    // across the finer rows. With no resolvable dim selected it shows the entity total.
    comboAttribution(row, combo) {
      // Downward combo: the number is the pivot descendant's own attribution.
      if (combo && combo.__pivotId != null) {
        const n = this.entityMap[combo.__pivotId]
        return (n && n.attribution && n.attribution.total_attribution) || 0
      }
      const contribs = this.contribsFor(row)
      if (!contribs.length) return row.attributed_lives || 0
      const ancDims = this.groupBy.filter(k => k.startsWith(ANCESTOR_PREFIX)).map(k => k.slice(ANCESTOR_PREFIX.length))
      if (ancDims.length === 0) return row.attributed_lives || 0
      const rdims = ancDims.filter(d => contribs.some(c => d in c.tuple))
      if (rdims.length === 0) return row.attributed_lives || 0
      let sum = 0
      for (const c of contribs) {
        if (rdims.every(d => (c.tuple[d] != null ? c.tuple[d] : UNTAGGED_VALUE) === combo[ANCESTOR_PREFIX + d])) {
          sum += c.cnt
        }
      }
      return sum
    },
    goToEntity(row) {
      this.$router.push(`/entity/${row.entity_id}`)
    },
    async handleExport() {
      const rows = this.flatComboRows
      if (!rows.length) return
      this.exporting = true
      try {
        const singular = this.entityTypeLabel.replace(/s$/, '')
        const data = rows.map(item => {
          const { row, combo } = item
          const m = this.metricSource(item)
          const out = {}
          out[singular] = row.name
          for (const key of this.groupBy) out[this.groupByLabel(key)] = combo[key] || ''
          out['Tags'] = (m.tags || []).map(t => t.name).join(', ')
          out['Engagement Score'] = m.engagement_composite != null ? Number(m.engagement_composite).toFixed(1) : ''
          out['Attributed Lives'] = this.comboAttribution(row, combo)
          out['Last Engagement'] = m.last_engagement_date || ''
          return out
        })
        const slug = this.selectedEntityType.toLowerCase().replace(/\s+/g, '_')
        await exportToExcel(data, `entity_summary_${slug}.xlsx`)
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>
